/* @flow */
import navigation from 'services/NavigationService';

import { apiService } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData.js';

import Form from '../_.extend';

const baseModelCalc: Function = (field: Object) => {
  const model = field.state.form.select('productDetail.model').value;
  const scode = field.state.form.select('productDetail.shortCode').value;

  if (model && scode) {
    field.state.form.update({
      productDetail: {
        baseModel: `${scode}${model}`,
      },
    });
  } else {
    field.state.form.update({
      productDetail: {
        baseModel: '',
      },
    });
  }
};

export const makeFields: Function = (
  itemData: Object,
  manufacturerID: string,
  shortCode: string,
  t: Function
) => {
  const usersTradingAccounts: string =
    itemData &&
    itemData.accounts instanceof Array &&
    itemData.accounts.map(account => account.id).join(',');

  return [
    {
      name: 'productDetail',
      label: t('general:products.labels.details'),
      fields: [
        {
          name: 'manufacturer',
          label: t('general:manufacturer'),
          rules: 'required|string',
          value: itemData.manufacturer_name || manufacturerID,
          disabled: itemData.id ? true : null,
          extra: {
            selectID: (itemData && itemData.owned_manufacturer_id) || manufacturerID,
            toggleToDisable: true,
            resetValue: itemData || null,
            resetValueFields: [
              {
                field: 'manufacturer',
                value: 'manufacturer_name',
              },
            ],
            setSelectedFields: [
              {
                field: 'shortCode',
                value: 'short_code',
              },
              {
                field: 'model',
                value: null,
              },
            ],
            toggleToTextField: true,
            isTextField: true,
            textTitle: 'Choose existing',
            title: 'Use original',
            buttonClick: 'model',
          },
        },
        {
          name: 'model',
          label: t('general:model'),
          rules: 'required|string',
          value: itemData.model,
          extra: {
            selectID: itemData ? itemData.id : null,
            parentId: itemData.owned_manufacturer_id,
            parentShortCode: shortCode,
            resetValue: itemData || null,
            resetValueFields: [
              {
                field: 'model',
                value: 'model',
              },
              {
                field: 'baseModel',
                value: 'base_model',
              },
              {
                field: 'description',
                value: 'product_group_description',
              },
              {
                field: 'fairMarketValue',
                value: 'fair_market_value',
              },
              {
                field: 'productStatus',
                value: 'status',
                condition: {
                  if: 'Approved',
                  ifVal: 'Approved',
                  elseVal: 'Not Approved',
                },
              },
            ],
            toggleToTextField: true,
            isTextField: true,
            textTitle: 'Choose existing',
            title: 'Use original',
            buttonClick: 'manufacturer',
            setSelectedFields: [
              {
                field: 'model',
                value: 'model',
              },
              {
                field: 'baseModel',
                value: 'base_model',
              },
              {
                field: 'description',
                value: 'product_group_description',
              },
              {
                field: 'fairMarketValue',
                value: 'fair_market_value',
              },
              {
                field: 'productStatus',
                value: 'status',
                condition: {
                  if: 'Approved',
                  ifVal: 'Approved',
                  elseVal: 'Not Approved',
                },
              },
            ],
          },
          hooks: {
            onChange(field) {
              baseModelCalc(field);
            },
          },
        },
        {
          name: 'description',
          label: t('general:assets.labels.model_description'),
          rules: 'required|string',
          value: itemData.product_group_description,
        },
        {
          name: 'productGroupCode',
          label: t('general:products.labels.group_code'),
          rules: 'string',
          value: itemData.productGroupCode,
        },
        {
          name: 'productGroupKey',
          label: t('general:projects.labels.group_key'),
          rules: 'string',
          value: itemData.productGroupKey,
        },
        {
          name: 'baseModel',
          label: t('general:products.labels.base_model'),
          rules: 'string',
          value: itemData.base_model ? itemData.base_model : `${shortCode}${itemData.model}`,
        },
        {
          name: 'fairMarketValue',
          label: t('general:labels.fair_market_value'),
          rules: 'numeric',
          value: itemData.fair_market_value,
        },
        {
          name: 'shortCode',
          value: shortCode || null,
          rules: 'string',
        },
        {
          name: 'productStatus',
          label: t('general:labels.status'),
          rules: 'required|string',
          ...(itemData
            ? {
                value: itemData.status === 'Approved' ? 'Approved' : 'Not Approved',
              }
            : { value: 'Not Approved' }),
          type: 'select',
          options: [
            {
              id: 'Approved',
              label: t('general:status.approved'),
            },
            {
              id: 'Not Approved',
              label: t('general:status.not_approved'),
            },
          ],
        },
        {
          name: 'globalList',
          label: t('general:labels.global_list'),
          rules: 'required|boolean',
          value: false,
        },
        {
          name: 'perfectProductNumber',
          label: t('general:products.labels.perfect_product_number'),
          value: itemData.perfect_number,
        },
        {
          name: 'd365ProductNumber',
          label: t('general:products.labels.d365_product_number'),
          value: itemData.d365_number,
        },
      ],
    },
    {
      name: 'assignOtherAccounts',
      label: itemData
        ? t('general:actions.assign_other_accounts')
        : t('general:actions.assign_accounts'),
      fields: [
        {
          name: 'accounts',
          label: t('general:actions.choose_trading_accounts_assigned_to_you'),
          type: 'textfield',
          rules: 'string',
          value: usersTradingAccounts || '',
          extra: {
            selectID: null,
          },
        },
      ],
    },
  ];
};

class ProductFormCreate extends Form {}

class ProductFormEdit extends Form {}

export const productCreate: Function = (fields: Array<*>, localState: Object, t: Function) => {
  const hooks: Object = {
    onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const {
        model,
        baseModel,
        description,
        fairMarketValue,
      }: Object = form.values().productDetail;
      const { accounts }: Object = form.values().assignOtherAccounts;
      const accountIds = accounts.split(',');

      const jsonObj: Object = {
        activity_tag: activities.createdProduct.tag, // eslint-disable-line camelcase
        owned_manufacturer_id: form.$('productDetail.manufacturer').extra.selectID,
        model,
        product_group_description: description,
        base_model: baseModel,
        fair_market_value: fairMarketValue,
        status: 'Approved',
        account_ids: accounts === '' ? [] : accountIds,
        global_list: form.$('productDetail.globalList').value,
      };
      !fairMarketValue && delete jsonObj.fair_market_value;

      apiService
        .createResource('products', jsonObj)
        .then((response: Object) => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })

        .catch((err: string) => {
          localState.isLoading = false;
          localState.responseMessage =
            err && err.errors && err.errors.name && err.errors.name.length > 0
              ? err.errors.name[0]
              : err && err.message;
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new ProductFormCreate(
    { fields },
    { hooks, name: t('general:actions.create_new_product') }
  );
};

export const productEdit: Function = (
  fields: Array<*>,
  itemData: Object,
  localState: Object,
  t: Function
) => {
  const hooks: Object = {
    onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const {
        model,
        baseModel,
        description,
        fairMarketValue,
        perfectProductNumber,
        d365ProductNumber,
        productStatus,
      }: Object = form.values().productDetail;
      const { accounts }: Object = form.values().assignOtherAccounts;
      const accountIds = accounts.split(',');
      const selectedProdId = form.$('productDetail.model').extra.selectID;

      if (itemData.status === 'Pending Review' && itemData.id !== selectedProdId) {
        apiService
          .putResource(`products/${selectedProdId}/merge`, {
            product_id: itemData.id,
          })
          .then((response: Object) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_success');

            setTimeout(() => {
              localState.showResponseMessage = false;
              /*eslint-disable max-nested-callbacks*/
              setTimeout(() => {
                // Snackbar animation 225ms
                navigation.goBack();
              }, 225);
            }, 1200);

            return response;
          })

          .catch((err: string) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_error');
            localState.hasSuccess = false;
            localState.hasError = true;
          });
      } else {
        apiService
          .putResource(`products/${itemData.id}`, {
            activity_tag: activities.edited.tag, // eslint-disable-line camelcase
            owned_manufacturer_id: form.$('productDetail.manufacturer').extra.selectID,
            model,
            product_group_description: description,
            base_model: form.$('productDetail.shortCode').value
              ? baseModel
              : form.$('productDetail.model').value,
            fair_market_value: fairMarketValue,
            d365_number: d365ProductNumber,
            perfect_number: perfectProductNumber,
            status: productStatus,
            account_ids: accounts === '' ? [] : accountIds,
            global_list: form.$('productDetail.globalList').value,
          })
          .then((response: Object) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_success');

            setTimeout(() => {
              localState.showResponseMessage = false;
              /*eslint-disable max-nested-callbacks*/
              setTimeout(() => {
                // Snackbar animation 225ms
                navigation.goBack();
              }, 225);
            }, 1200);

            return response;
          })

          .catch((err: string) => {
            localState.isLoading = false;
            localState.responseMessage = t('general:messages.save_error');
            localState.hasSuccess = false;
            localState.hasError = true;
          });
      }
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new ProductFormEdit({ fields }, { hooks, name: t('general:actions.edit_product') });
};
